// /dev/states (§13.8): every significant component in every state on one
// scrollable page, so the agent can screenshot any UI state without walking a
// flow. Compiled out of production builds via the IS_DEV route guard.

import { distill } from "../lib/distiller.ts";
import { daveTables, sarahTables } from "../../fixtures/data.ts";
import { Page } from "../components/Layout.tsx";
import { RatingHistogram, DecadeBars, HeatStrip, ScoreDial, VennCounts, StatTile } from "../components/Charts.tsx";
import { SoloStats } from "../components/SoloStats.tsx";
import { EnrichedStats } from "../components/EnrichedStats.tsx";
import { Analyzing } from "../components/Analyzing.tsx";
import { TasteSignature } from "../components/TasteSignature.tsx";
import { useEnrichedStats } from "../lib/useEnrichedStats.ts";
import { ShareCardPreview } from "../components/ShareCardPreview.tsx";
import { PicksGenerating } from "../components/PicksPanel.tsx";
import { BlendView } from "../components/BlendView.tsx";
import { PosterRow } from "../components/Poster.tsx";
import { PosterCard } from "../components/brand.tsx";
import { InviteBlock } from "../components/InviteBlock.tsx";

const dave = distill(daveTables(), { exportedAt: "2026-07-01" });
const sarah = distill(sarahTables(), { exportedAt: "2026-07-01" });

// A tiny 1-film profile for extremes.
const tinyProfile = distill(
  { watched: [{ Name: "Solo Film", Year: "2001", "Letterboxd URI": "https://boxd.it/one" }] },
  { exportedAt: "2026-07-01" },
);

function GalleryEnriched() {
  const { stats, progress } = useEnrichedStats(dave);
  return <EnrichedStats stats={stats} progress={progress} />;
}

function State({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <section data-testid={`states-gallery-${name}`} className="border-b border-border-soft py-8">
      <h2 className="mb-4 text-xs uppercase tracking-widest text-gold-500">{name}</h2>
      {children}
    </section>
  );
}

export default function DevStates() {
  return (
    <Page testid="screen-dev-states" wide>
      <h1 className="text-2xl font-semibold text-ink-900">States gallery</h1>

      <State name="score-dials">
        <div className="flex flex-wrap gap-6">
          {[3, 55, 79, 92, 98].map((s) => (
            <ScoreDial key={s} score={s} />
          ))}
        </div>
      </State>

      <State name="stat-tiles">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile value={1043} label="Films watched" />
          <StatTile value={891} label="Rated" />
          <StatTile value={212} label="Watchlist" />
          <StatTile value={7} label="Longest streak" />
        </div>
      </State>

      <State name="charts">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="card">
            <RatingHistogram hist={dave.aggregates.ratingHist} />
          </div>
          <div className="card">
            <DecadeBars byDecade={dave.aggregates.byDecade} />
          </div>
          <div className="card">
            <HeatStrip loggedByMonth={dave.aggregates.loggedByMonth} />
          </div>
        </div>
      </State>

      <State name="venn">
        <VennCounts onlyA={10} both={12} onlyB={8} nameA="Dave" nameB="Sarah" />
      </State>

      <State name="posters">
        <PosterRow>
          <PosterCard title="Aftersun" year={2022} width={120} />
          <PosterCard title="The Substance" year={2024} badge="watchlist" width={120} />
          <PosterCard title="A Very Long Film Title That Should Wrap Nicely" year={1998} width={120} />
          <PosterCard title="Parasite" year={2019} rating={4.8} overlap width={120} />
        </PosterRow>
      </State>

      <State name="invite-block">
        <InviteBlock shareSlug="x7Kf9q2m" />
      </State>

      <State name="solo-stats">
        <SoloStats profile={dave} />
      </State>

      <State name="solo-stats-tiny">
        <SoloStats profile={tinyProfile} />
      </State>

      <State name="taste-signature">
        <TasteSignature profile={dave} signatureGenre="Drama" contrarian={0.9} />
      </State>

      <State name="analyzing">
        <div className="card">
          <h3 className="section-title">Deep focus</h3>
          <Analyzing done={7} total={16} />
        </div>
      </State>

      <State name="enriched-stats">
        <div className="card">
          <GalleryEnriched />
        </div>
      </State>

      <State name="picks-generating">
        <div className="rounded-xl border border-red-600/30 bg-card p-4">
          <PicksGenerating mode="newground" regenerating={false} />
        </div>
      </State>

      <State name="share-cards">
        <ShareCardPreview />
      </State>

      <State name="blend-full">
        <BlendView profileA={dave} profileB={sarah} nameA="Dave" nameB="Sarah" />
      </State>
    </Page>
  );
}
